import React from 'react';
import PropTypes from 'prop-types';

import ReusableTable from 'components/common/ReusableTable';
import ReusableSnackbar from 'components/common/ReusableSnackbar';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import history from 'wrappers/history';
import styles from './style.module.scss';

class SystemSettingsPage extends React.Component {
  constructor(props) {
    super(props);

    if (!this.props.isLoggedIn) {
      history.push('/login');
    } else if (!this.props.isAdmin) {
      history.push('/forbidden');
    }

    this.state = {
      snackbarOpen: false,
      snackbarVariant: 'success',
      snackbarMessage: '',
      contactUsRecipientColumns: [
        {
          title: 'Position',
          field: 'position',
          editable: 'never',
          defaultSort: 'asc',
        },
        {
          title: 'Label',
          field: 'label',
        },
        {
          title: 'Email',
          field: 'email',
        },
        {
          title: 'Active',
          field: 'active',
          type: 'boolean',
        },
      ],
      contactUsRecipientOptions: {
        filtering: false,
        padding: 'dense',
        maxBodyHeight: 500,
        selection: false,
        hideDeleteButton: true,
      },
      contactUsRecipientActions: [
        {
          icon: KeyboardArrowUpIcon,
          tooltip: 'Move up',
          position: 'row',
          onClick: (event, rowData) => this.moveContactUsRecipientUp(event, rowData),
        },
        {
          icon: KeyboardArrowDownIcon,
          tooltip: 'Move down',
          position: 'row',
          onClick: (event, rowData) => this.moveContactUsRecipientDown(event, rowData),
        },
        {
          icon: 'replay',
          tooltip: 'Refetch Data',
          isFreeAction: true,
          onClick: () => this.props.getAdminContactUsRecipients(),
        },
      ],
      contactUsRecipientEditable: {
        isEditable: () => true,
        isDeletable: () => false,
        onRowAdd: (newRecipient) => new Promise((resolve, reject) => {
          const recipient = {
            ...newRecipient,
            active: newRecipient.active === undefined ? true : newRecipient.active,
            position: newRecipient.position || this.maxContactUsRecipientPosition() + 1,
          };
          const errorMessage = this.validateContactUsRecipient(recipient);
          if (errorMessage === '') {
            this.props.createContactUsRecipient(recipient);
            this.showSnackbar('success', `Contact Us recipient ${recipient.label} successfully added.`);
            resolve();
          } else {
            this.showSnackbar('error', errorMessage);
            reject();
          }
        }),
        onRowUpdate: (updatedRecipient) => new Promise((resolve, reject) => {
          const errorMessage = this.validateContactUsRecipient(updatedRecipient);
          if (errorMessage === '') {
            this.props.updateContactUsRecipient(updatedRecipient);
            this.showSnackbar('success', `Contact Us recipient ${updatedRecipient.label} successfully updated.`);
            resolve();
          } else {
            this.showSnackbar('error', errorMessage);
            reject();
          }
        }),
        onRowDelete: () => new Promise((resolve) => {
          resolve();
        }),
      },
    };
  }

  componentDidMount() {
    if (this.props.contactUsRecipients.length === 0) {
      this.props.getAdminContactUsRecipients();
    }
  }

  get sortedContactUsRecipients() {
    return [...this.props.contactUsRecipients].sort((a, b) => a.position - b.position);
  }

  maxContactUsRecipientPosition = () => {
    let maxPosition = 0;
    this.props.contactUsRecipients.forEach(recipient => {
      if (recipient.position > maxPosition) {
        maxPosition = recipient.position;
      }
    });
    return maxPosition;
  };

  validateContactUsRecipient = (recipient) => {
    let errorMessage = '';
    if (!recipient.label || recipient.label.trim() === '') {
      errorMessage += 'Label cannot be empty.\n';
    }
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(recipient.email || '')) {
      errorMessage += 'Email is not in a valid format or is empty.\n';
    }
    if (!Number.isInteger(Number(recipient.position)) || Number(recipient.position) < 1) {
      errorMessage += 'Position must be a positive integer.\n';
    }
    return errorMessage.trim();
  };

  showSnackbar = (variant, message) => {
    this.setState({
      snackbarOpen: true,
      snackbarVariant: variant,
      snackbarMessage: message,
    });
  };

  closeSnackbar = () => {
    this.setState({
      snackbarOpen: false,
      snackbarMessage: '',
    });
  };

  moveContactUsRecipientUp = (_event, recipient) => {
    const sortedRecipients = this.sortedContactUsRecipients;
    const recipientIndex = sortedRecipients.findIndex(sortedRecipient => sortedRecipient.id === recipient.id);
    if (recipientIndex <= 0) {
      return;
    }

    const previousRecipient = sortedRecipients[recipientIndex - 1];
    this.swapContactUsRecipientPositions(recipient, previousRecipient);
  };

  moveContactUsRecipientDown = (_event, recipient) => {
    const sortedRecipients = this.sortedContactUsRecipients;
    const recipientIndex = sortedRecipients.findIndex(sortedRecipient => sortedRecipient.id === recipient.id);
    if (recipientIndex === -1 || recipientIndex >= sortedRecipients.length - 1) {
      return;
    }

    const nextRecipient = sortedRecipients[recipientIndex + 1];
    this.swapContactUsRecipientPositions(recipient, nextRecipient);
  };

  swapContactUsRecipientPositions = (firstRecipient, secondRecipient) => {
    this.props.updateContactUsRecipient({
      ...firstRecipient,
      position: secondRecipient.position,
    });
    this.props.updateContactUsRecipient({
      ...secondRecipient,
      position: firstRecipient.position,
    });
  };

  render() {
    return (
      <div id="maincontent">
        <div className="wdn-band">
          <div className="wdn-inner-wrapper">
            <h2 className={styles['no-margin-top']}>
              System Settings
            </h2>
            <hr className={styles['divider']} />
            <div className={styles['table-container']}>
              <ReusableTable
                title="Contact Us Recipients"
                columns={this.state.contactUsRecipientColumns}
                data={this.sortedContactUsRecipients}
                options={this.state.contactUsRecipientOptions}
                editable={this.state.contactUsRecipientEditable}
                isLoading={this.props.contactUsRecipientsLoading}
                actions={this.state.contactUsRecipientActions}
              />
            </div>
            <ReusableSnackbar
              open={this.state.snackbarOpen}
              onClose={this.closeSnackbar}
              variant={this.state.snackbarVariant}
              message={this.state.snackbarMessage}
            />
          </div>
        </div>
      </div>
    );
  }
}

SystemSettingsPage.propTypes = {
  contactUsRecipients: PropTypes.array.isRequired,
  contactUsRecipientsLoading: PropTypes.bool.isRequired,
  getAdminContactUsRecipients: PropTypes.func.isRequired,
  createContactUsRecipient: PropTypes.func.isRequired,
  updateContactUsRecipient: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
};

export default SystemSettingsPage;
